// ✅ src/pages/OrderManagement/RateApprovalPrint.jsx  (FULL FILE - FIXED FOR YOUR STORAGE KEY)
import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (v) =>
  toNum(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const readPayload = () => {
  try {
    const raw = sessionStorage.getItem("RateApprovalPrint_payload");
    if (!raw) return { rows: [], header: {} };

    const payload = JSON.parse(raw) || {};

    // ✅ your API might return: { success:true, selectedRows, header }
    // or: { success:true, data:{ selectedRows, header } }
    const rows =
      (Array.isArray(payload.selectedRows) && payload.selectedRows) ||
      (Array.isArray(payload?.data?.selectedRows) && payload.data.selectedRows) ||
      (Array.isArray(payload?.rows) && payload.rows) ||
      (Array.isArray(payload?.data?.rows) && payload.data.rows) ||
      [];

    const header =
      payload.header ||
      payload?.data?.header ||
      {
        customerName: payload.fld_customername || payload?.data?.fld_customername || "",
        customerAddress: payload.fld_customeraddress || payload?.data?.fld_customeraddress || "",
        projectName: payload.fld_projectname || payload?.data?.fld_projectname || "",
        preparedBy: payload.fld_preparedby || payload?.data?.fld_preparedby || "",
      };

    return { rows, header };
  } catch {
    return { rows: [], header: {} };
  }
};

export default function RateApprovalPrint() {
  const navigate = useNavigate();
  const [{ rows, header }, setState] = useState({ rows: [], header: {} });

  useEffect(() => {
    setState(readPayload());
  }, []);

  const headerInfo = useMemo(() => {
    // If header not provided, derive from first row like ASP
    const first = rows?.[0] || {};
    return {
      customerName: String(header?.customerName ?? first.fld_customername ?? "").trim(),
      customerAddress: String(header?.customerAddress ?? first.fld_customeraddress ?? "").trim(),
      projectName: String(header?.projectName ?? first.fld_projectname ?? "").trim(),
      preparedBy: String(header?.preparedBy ?? first.fld_preparedby ?? "").trim(),
    };
  }, [rows, header]);

  const totals = useMemo(() => {
    const total = (rows || []).reduce((sum, r) => sum + toNum(r.fld_amount), 0);
    return { total };
  }, [rows]);

  const handleBack = () => {
    if (window.opener) {
      window.close();
      return;
    }
    navigate(-1);
  };

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page { padding: 0 !important; }
          .card { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      {/* Top Action Bar (hidden in print) */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
          >
            <FaArrowLeft />
            Back
          </button>

          <div className="font-semibold">Rate Approval Print</div>

          <button
            onClick={handlePrint}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
          >
            <FaPrint />
            Print
          </button>
        </div>
      </div>

      <div className="page max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="card border border-gray-300 rounded-xl p-4">
          <div className="text-center border-b border-gray-300 pb-3">
            <div className="text-lg font-bold text-red-600">Nissi Infotech</div>
            <div className="text-sm">Nissi</div>
            <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
            <div className="text-xs">
              3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,India
            </div>
            <div className="text-xs">
              Phone No : 9003744644 Email : infraconsoft-implementation@nissiinfotech.in
            </div>
          </div>

          <div className="text-center py-2 font-bold border-b border-gray-300">
            RATE APPROVAL
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 text-sm">
            <div>
              <div className="font-semibold">Customer</div>
              <div>{headerInfo.customerName || "-"}</div>
              <div className="whitespace-pre-line">{headerInfo.customerAddress || "-"}</div>
            </div>
            <div>
              <div className="font-semibold">Project</div>
              <div>{headerInfo.projectName || "-"}</div>
              <div className="mt-2">
                <span className="font-semibold">Prepared By:</span> {headerInfo.preparedBy || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border border-gray-300 rounded-xl mt-4 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-3 py-2 text-left w-12">S.No</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Nature of Work / Item</th>
                  <th className="px-3 py-2 text-left w-20">Unit</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                  <th className="px-3 py-2 text-right w-28">Rate</th>
                  <th className="px-3 py-2 text-right w-32">Amount</th>
                  <th className="px-3 py-2 text-left w-24">From</th>
                  <th className="px-3 py-2 text-left w-24">To</th>
                  <th className="px-3 py-2 text-left w-40">Remarks</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-gray-600">
                      No data selected for print.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-gray-200 align-top">
                      <td className="px-3 py-2">{idx + 1}</td>
                      <td className="px-3 py-2 text-blue-700 font-medium">
                        {r.fld_projectcategoryname || ""}
                      </td>
                      <td className="px-3 py-2">
                        <div className="whitespace-pre-line leading-relaxed">
                          {r.fld_work || ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Type: {r.fld_type || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-2">{r.fld_unit || ""}</td>
                      <td className="px-3 py-2 text-right">{fmt2(r.fld_Qty)}</td>
                      <td className="px-3 py-2 text-right">{fmt2(r.fld_rate)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt2(r.fld_amount)}</td>
                      <td className="px-3 py-2">{r.fld_fromdate || ""}</td>
                      <td className="px-3 py-2">{r.fld_todate || ""}</td>
                      <td className="px-3 py-2">{r.fld_remarks || ""}</td>
                    </tr>
                  ))
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="px-3 py-3 text-right font-semibold">
                      Total
                    </td>
                    <td className="px-3 py-3 text-right font-bold">{fmt2(totals.total)}</td>
                    <td colSpan={3} className="px-3 py-3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="text-xs text-gray-500 text-center mt-6">
          * This is a system generated document.
        </div>
      </div>
    </div>
  );
}
